import { useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';
import PercentageField from '@/components/form/PercentageField.tsx';
import FormSection from '@/features/investments/form/components/FormSection.tsx';
import { InvestmentFormData } from '@/features/investments/form/investment-form.schema.ts';
import {
  getDetailedPurchaseCostsTotal,
  hasDetailedPurchaseCosts,
} from '@/features/investments/form/utils.ts';
import { formatCurrency } from '@/utils/misc.ts';

type CostsMode = 'simple' | 'detailed';

const dateFieldSx = {
  '& .MuiInputBase-input': {
    padding: '16.5px 14px',
  },
  '& input[type="date"]': {
    textAlign: 'left',
  },
  '& input[type="date"]::-webkit-date-and-time-value': {
    textAlign: 'left',
  },
  '& input[type="date"]::-webkit-datetime-edit': {
    padding: 0,
  },
  '& input[type="date"]::-webkit-datetime-edit-fields-wrapper': {
    padding: 0,
  },
};

const PurchaseSection = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<InvestmentFormData>();

  const [
    acquisitionPrice,
    renovationCosts,
    furnitureEquipmentCosts,
    purchaseCostsTotal,
    itpTax,
    notaryFees,
    registryFees,
    agencyFees,
    legalFees,
    otherTransactionCosts,
  ] = useWatch({
    control,
    name: [
      'acquisitionPrice',
      'renovationCosts',
      'furnitureEquipmentCosts',
      'purchaseCostsTotal',
      'itpTax',
      'notaryFees',
      'registryFees',
      'agencyFees',
      'legalFees',
      'otherTransactionCosts',
    ],
  });

  const hasDetailed = hasDetailedPurchaseCosts({
    itpTax,
    notaryFees,
    registryFees,
    agencyFees,
    legalFees,
    otherTransactionCosts,
  });

  const [costsMode, setCostsMode] = useState<CostsMode>(hasDetailed ? 'detailed' : 'simple');

  const detailedPurchaseCosts = getDetailedPurchaseCostsTotal({
    itpTax,
    notaryFees,
    registryFees,
    agencyFees,
    legalFees,
    otherTransactionCosts,
  });

  const effectivePurchaseCosts =
    costsMode === 'detailed' ? detailedPurchaseCosts : (purchaseCostsTotal ?? 0);

  const totalInvestment =
    (acquisitionPrice ?? 0) +
    (renovationCosts ?? 0) +
    (furnitureEquipmentCosts ?? 0) +
    effectivePurchaseCosts;

  return (
    <FormSection title="3. Compra" description="Importes clave de compra y estructura de costes.">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Controller
          name="purchaseDate"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Fecha de compra / escritura"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
              error={!!errors.purchaseDate}
              helperText={errors.purchaseDate?.message}
            />
          )}
        />
        <Controller
          name="ownershipPercentage"
          control={control}
          render={({ field: { value, onChange } }) => (
            <PercentageField
              value={value}
              onChange={onChange}
              label="Titularidad %"
              helperText={errors.ownershipPercentage?.message}
              error={!!errors.ownershipPercentage}
            />
          )}
        />
      </Stack>

      <Controller
        name="acquisitionPrice"
        control={control}
        render={({ field: { value, onChange } }) => (
          <EurCurrencyField
            label="Precio de compra"
            value={value}
            onChange={onChange}
            error={!!errors.acquisitionPrice}
            helperText={errors.acquisitionPrice?.message}
          />
        )}
      />

      <Controller
        name="renovationCosts"
        control={control}
        render={({ field: { value, onChange } }) => (
          <EurCurrencyField
            label="Reforma"
            value={value}
            onChange={onChange}
            helperText="Opcional"
          />
        )}
      />

      <Controller
        name="furnitureEquipmentCosts"
        control={control}
        render={({ field: { value, onChange } }) => (
          <EurCurrencyField
            label="Mobiliario y equipamiento"
            value={value}
            onChange={onChange}
            helperText="Opcional. Muebles, electrodomésticos y equipamiento inicial"
          />
        )}
      />

      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" fontWeight={600}>
            Gastos de compra
          </Typography>
          <ToggleButtonGroup
            value={costsMode}
            exclusive
            onChange={(_, value: CostsMode | null) => {
              if (value) setCostsMode(value);
            }}
            size="small"
          >
            <ToggleButton value="simple" sx={{ px: 2, py: 0.5, textTransform: 'none' }}>
              Total
            </ToggleButton>
            <ToggleButton value="detailed" sx={{ px: 2, py: 0.5, textTransform: 'none' }}>
              Desglose
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {costsMode === 'simple' ? (
          <Controller
            name="purchaseCostsTotal"
            control={control}
            render={({ field: { value, onChange } }) => (
              <EurCurrencyField
                label="Gastos de compra totales"
                value={value}
                onChange={onChange}
                helperText="Opcional"
              />
            )}
          />
        ) : (
          <Stack spacing={2}>
            <Controller
              name="itpTax"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField label="ITP individual" value={value} onChange={onChange} />
              )}
            />
            <Controller
              name="notaryFees"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField label="Gastos de notaría" value={value} onChange={onChange} />
              )}
            />
            <Controller
              name="registryFees"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField label="Gastos de registro" value={value} onChange={onChange} />
              )}
            />
            <Controller
              name="agencyFees"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField label="Comisión de agencia" value={value} onChange={onChange} />
              )}
            />
            <Controller
              name="legalFees"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField label="Honorarios legales" value={value} onChange={onChange} />
              )}
            />
            <Controller
              name="otherTransactionCosts"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Otros gastos de transacción"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Stack>
        )}
      </Stack>

      <Typography variant="subtitle1" fontWeight={700} color="primary.main">
        Inversión total: {formatCurrency(totalInvestment)}
      </Typography>

      <Controller
        name="initialCostComplete"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormControlLabel
            control={
              <Checkbox checked={value} onChange={(event) => onChange(event.target.checked)} />
            }
            label={
              <Stack>
                <Typography variant="body2">Ya no falta ningún coste inicial</Typography>
                <Typography variant="caption" color="text.secondary">
                  Márcalo cuando el importe de arriba sea definitivo. La rentabilidad sobre lo
                  invertido deja de mostrarse como provisional.
                </Typography>
              </Stack>
            }
            sx={{ alignItems: 'flex-start', mr: 0 }}
          />
        )}
      />
    </FormSection>
  );
};

export default PurchaseSection;
